<template>
    <section class="g-container || o-band">
        <div class="o-container">

            <app-intro class="u-d-flex align-items-center justify-content-between">
                <h3 class="u-mb-0">Abonnement bewerken</h3>

                <router-link to="/admin/subscriptions" class="c-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18.589" height="12.388" viewBox="0 0 18.589 12.388">
                        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
                            <path d="M17.589 6.223H4.714"/>
                            <path d="M7.439 10.989L1.001 6.195l6.438-4.8" stroke-linejoin="round"/>
                            <path d="M7.439 10.99L5.894 6.195l1.545-4.8" stroke-linejoin="round"/>
                        </g>
                    </svg>

                    Terug <span class="u-hidden-xsl-down">naar overzicht</span>
                </router-link>
            </app-intro>

            <div class="c-loading" v-if="loading">
                <spinner/>
                Bezig met laden van het abonnement...
            </div>

            <transition-group name="fade" tag="section" v-if="!loading">
                <form v-on:submit.prevent="handleSubmit" class="form form---tint" novalidate key="form">
                    <div class="g-gap:2-xs g-gap:2-sm>sm || o-card">

                        <div class="form-fieldset" v-if="form.product">
                            <h4 class="u-mb-0">Gegevens</h4>

                            <div class="form-group">
                                <label class="u-sr-only">Product</label>
                                <div class="form-control-static">{{ form.product }}</div>
                            </div>
                        </div>

                        <div class="form-fieldset">
                            <h4 class="u-mb-0">Vestigingen</h4>

                            <div class="table table--small table--bordered" v-if="locations.length > 0">
                                <div class="table__thead">
                                    <div class="table__tr" v-for="location in locations" :key="`thead-${location.VestigingID}`">
                                        <div class="table__th">Abonnementnr</div>
                                        <div class="table__th">Naam</div>
                                        <div class="table__th">Startdatum</div>
                                        <div class="table__th">Einddatum</div>
                                        <div class="table__th">Licenties</div>
                                        <div class="table__th"></div>
                                    </div>
                                </div>
                                <div class="table__body">
                                    <div class="table__tr" v-for="location in locations" :key="`tbody-${location.VestigingID}`">
                                        <div class="table__td || delta || u-font-weight-normal">{{ location.AbonnementID > 0 ? location.AbonnementID : '-' }}</div>
                                        <div class="table__td || delta || u-font-weight-normal">{{ location.Vestigingsnaam }}</div>
                                        <div class="table__td || delta || u-font-weight-normal">{{ location.Startdatum ? location.Startdatum : '-' }}</div>
                                        <div class="table__td || delta || u-font-weight-normal">{{ location.Stopdatum ? location.Stopdatum : '-' }}</div>
                                        <div class="table__td || delta || u-font-weight-normal">
                                            <router-link :to="`/admin/licenses/${location.ProductID}/${location.AbonnementID}`" v-if="location.Licenties > 0">Bekijk licenties</router-link>
                                            <span v-else>-</span>
                                        </div>

                                        <div class="table__td" v-if="location.AbonnementID === 0">
                                            <a href="#0" class="c-link || u-font-weight-normal" :data-location-id="location.VestigingID" :data-product-id="location.ProductID" :data-location="location.Vestigingsnaam" @click.prevent="showModal">
                                                Toevoegen
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10.792 10.791">
                                                    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
                                                        <path d="M1 5.396h8.791"/>
                                                        <path d="M5.396 1v8.791"/>
                                                    </g>
                                                </svg>
                                            </a>
                                        </div>

                                        <div class="table__td" v-if="location.AbonnementID > 0 && !location.Stopdatum">
                                            <a href="#0" class="c-link c-link--danger || u-font-weight-normal" :data-sub-id="location.AbonnementID" :data-location-id="location.VestigingID" :data-product-id="location.ProductID" :data-location="location.Vestigingsnaam" @click.prevent="showModalConfirm">
                                                Stopzetten
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 9.045 9.045">
                                                    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
                                                        <path d="M1.415 1.414L7.63 7.631"/>
                                                        <path d="M7.632 1.414L1.415 7.631"/>
                                                    </g>
                                                </svg>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else>
                                <p>Er zijn nog geen vestigingen aangemaakt. Maak er <router-link to="/admin/locations/add">hier</router-link> een aan.</p>
                            </div>

                        </div>
                    </div>
                </form>

                <modal key="modal" v-show="modal.visible" @close="closeModal">
                    <template v-slot:header>
                        <h4 class="u-mb-0">Abonnement toevoegen</h4>
                        <span class="delta || u-font-weight-normal || u-text-primary">{{ modal.location }}</span>
                    </template>

                    <template v-slot:body>
                        <form v-on:submit.prevent="addSubscription" class="form">
                            <input type="hidden" name="VestigingID" :value="modal.VestigingID" readonly />
                            <input type="hidden" name="ProductID" :value="modal.ProductID" readonly />

                            <div class="form-checkboxes">
                                <div class="custom-checkbox">
                                    <label for="register-privacy" class="custom-control-label">
                                        <input type="checkbox" v-model="form.privacy" name="AkkoordVoorwaarden" value="Ja" id="register-privacy" class="custom-control-input" />
                                        <span class="custom-control-name custom-control-name--reset">Ik ga akkoord met de <a href="https://www.addsoft.nl/voorwaarden" target="_blank" class="u-font-weight-bold">abonnementsvoorwaarden</a></span>
                                    </label>
                                </div>

                                <div class="custom-checkbox" v-if="product.VerwerkersovereenkomstVerplicht">
                                    <label for="register-privacy1" class="custom-control-label">
                                        <input type="checkbox" v-model="form.privacy1" name="AkkoordVerwerkersovereenkomst" value="Ja" id="register-privacy1" class="custom-control-input" />
                                        <span class="custom-control-name custom-control-name--reset">Ik ga akkoord met de <a href="https://www.addsoft.nl/voorwaarden" target="_blank" class="u-font-weight-bold">verwerkersovereenkomst</a></span>
                                    </label>
                                </div>
                            </div>

                            <button type="submit" class="c-btn c-btn--info || form-submit-btn" :class="{ 'disabled': product.VerwerkersovereenkomstVerplicht ? !form.privacy || !form.privacy1 : !form.privacy }">
                                Abonnement toevoegen

                                <svg xmlns="http://www.w3.org/2000/svg" width="14.875" height="14.875" viewBox="0 0 14.875 14.875">
                                    <g fill="none" stroke="#fff" stroke-linecap="round" stroke-width="2">
                                        <path d="M1 7.438h12.875"/>
                                        <path d="M7.437 1v12.875"/>
                                    </g>
                                </svg>
                            </button>
                        </form>
                    </template>
                </modal>

                <modal key="modal-confirm" v-show="modalConfirm.visible" @close="closeModalConfirm">
                    <template v-slot:header>
                        <h4 class="u-mb-0">Weet je zeker dat je dit abonnement wilt stopzetten?</h4>
                        <span class="delta || u-font-weight-normal || u-text-primary">{{ modal.location }}</span>
                    </template>

                    <template v-slot:body>
                        <form v-on:submit.prevent="stopSubscription" class="form">

                            <div class="u-d-flex justify-content-end">
                                <button type="submit" href="#0" class="c-btn c-btn--danger">
                                    Ja, abonnement stopzetten

                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 9.045 9.045">
                                        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2">
                                            <path d="M1.415 1.414L7.63 7.631"/>
                                            <path d="M7.632 1.414L1.415 7.631"/>
                                        </g>
                                    </svg>
                                </button>
                            </div>
                        </form>
                    </template>
                </modal>
            </transition-group>

        </div>
    </section>
</template>

<script>
    import { mapState } from 'vuex';
    import AppIntro from '../../components/app/AppIntro.vue';
    import Modal from '../../components/Modal.vue';
    import Spinner from '../../components/Spinner.vue';
    import normalizeFormData from '../../helpers/normalizeFormData';
    import { subscriptionService } from '../../services';

    export default {
        components: { Spinner, Modal, AppIntro },
        data () {
            return {
                loading: true,
                submitted: false,
                product: {},
                locations: [],
                form: {
                    product: '',
                    Startdatum: '',
                },
                modal: {
                    visible: false,
                    VestigingID: '',
                    ProductID: '',
                    location: '',
                },
                modalConfirm: {
                    visible: false,
                },
            };
        },
        computed: {
            ...mapState('subscription', ['status']),
            ...mapState('subscription', {
                products: 'list',
            }),
        },
        async created () {
            // get subscriptions
            await this.$store.dispatch('subscription/list');

            // get the subscription
            await this.getSubscription();

            // get extra data
            this.product = this.products.find((product) => product.ProductID === Number(this.$route.params.id));
            this.product.VerwerkersovereenkomstVerplicht = this.product.VerwerkersovereenkomstVerplicht === 'Ja';

            // reset loading
            this.loading = false;
        },
        methods: {
            getSubscription () {
                return subscriptionService
                    .get(this.$route.params.id)
                    .then((result) => {
                        const product = result.find((item) => item.Productnaam);
                        this.form.product = product.Productnaam;
                        this.locations = result;
                    }).catch(() => {
                        this.loading = false;
                    });
            },
            showModal ($event) {
                this.modal.ProductID = $event.target.dataset.productId;
                this.modal.VestigingID = $event.target.dataset.locationId;
                this.modal.location = $event.target.dataset.location;
                this.modal.visible = true;
            },
            showModalConfirm ($event) {
                this.modal.ProductID = $event.target.dataset.productId;
                this.modal.VestigingID = $event.target.dataset.locationId;
                this.modal.AbonnementID = $event.target.dataset.subId;
                this.modal.location = $event.target.dataset.location;
                this.modalConfirm.visible = true;
            },
            closeModal () {
                this.modal.location = '';
                this.modal.visible = false;
            },
            closeModalConfirm () {
                this.modal.location = '';
                this.modalConfirm.visible = false;
            },
            async addSubscription (e) {
                const data = normalizeFormData(e.target);

                await this.$store.dispatch('subscription/create', data);

                this.getSubscription();
                this.closeModal();
            },
            async stopSubscription (e) {
                const id = this.modal.AbonnementID;
                const data = normalizeFormData(e.target);

                await this.$store.dispatch('subscription/stop', { id, data });

                this.getSubscription();
                this.closeModalConfirm();
            },
        },

    };
</script>

<style lang="scss" scoped>
    @import "../../assets/scss/_essentials.scss";

    .table {
        @include media-breakpoint-only(sm) {
            grid-template-columns: rem(150) 1fr;
        }

        &__tr {
            @include media-breakpoint-up(md) {
                grid-template-columns: 0.75fr 1.25fr 1fr 1fr 0.75fr rem(130);
            }
        }
    }
</style>
